export class SlideshowRotation {
  constructor({
    interval = 6000,
    advance,
    schedule = (callback, delay) => setTimeout(callback, delay),
    cancel = (timer) => clearTimeout(timer),
    onState = () => {},
  }) {
    this.interval = Math.max(3000, Math.min(10000, Number(interval) || 6000));
    this.advance = advance;
    this.schedule = schedule;
    this.cancel = cancel;
    this.onState = onState;
    this.requested = false;
    this.blocks = new Set();
    this.timer = null;
    this.disposed = false;
    this.generation = 0;
  }
  setInterval(value) {
    const interval = Math.max(3000, Math.min(10000, Number(value) || 6000));
    if (interval === this.interval) return;
    this.interval = interval;
    this.sync();
  }
  get running() {
    return this.requested && !this.blocks.size && !this.disposed;
  }
  request(value) {
    this.requested = !!value;
    this.sync();
  }
  block(reason, value) {
    if (this.blocks.has(reason) === !!value) return;
    value ? this.blocks.add(reason) : this.blocks.delete(reason);
    this.sync();
  }
  resumeAfterNavigation() {
    this.blocks.delete('controls-hover');
    this.blocks.delete('controls-focus');
    this.sync(2000);
  }
  sync(delay = this.interval) {
    const generation = ++this.generation;
    if (this.timer !== null) this.cancel(this.timer);
    this.timer = null;
    if (this.running)
      this.timer = this.schedule(() => {
        if (generation !== this.generation) return;
        this.timer = null;
        if (!this.running) return;
        this.advance();
        this.sync();
      }, delay);
    this.onState({ requested: this.requested, running: this.running });
  }
  dispose() {
    this.disposed = true;
    this.sync();
  }
}

const { MediaTrack, TrackInput, wrapSlide, SlideLinkGesture, restoreFiles } = await import(
  globalThis.MeriAssets?.mediaTrack || './media-track.js'
);
export { SlideLinkGesture, wrapSlide, MediaTrack as SlideshowTrack };

export class MeriSlideshow extends (globalThis.HTMLElement || class {}) {
  get current() {
    return this.controller?.index ?? 0;
  }
  static get observedAttributes() {
    return ['data-interval'];
  }
  attributeChangedCallback(name, previous, value) {
    if (name === 'data-interval' && previous !== value) this.rotation?.setInterval(value);
  }
  connectedCallback() {
    const track = this.querySelector('[data-slideshow-track]');
    if (this.controller?.matches(track, [...(track?.querySelectorAll('[data-slide]') || [])])) return;
    try {
      this.initialize();
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }
  initialize() {
    const selectedId = this.selectedBlockId;
    const requested = this.rotation?.requested;
    this.cleanup();
    this.track = this.querySelector('[data-slideshow-track]');
    if (!this.track) return;
    const rail = this.track.querySelector(':scope > [data-slideshow-rail]');
    this.slides = [...(rail || this.track).querySelectorAll(':scope > [data-slide]')];
    if (!this.slides.length) return;
    this.contentTargets = this.slides.map((slide) => slide.querySelector('.meri-slideshow__content')).filter(Boolean);
    this.abort = new AbortController();
    const signal = this.abort.signal;
    const listen = (target, type, handler, options = {}) =>
      target?.addEventListener(type, handler, { ...options, signal });
    this.controls = this.querySelector('[data-slideshow-controls]');
    this.counter = this.querySelector('[data-slideshow-counter]');
    this.toggle = this.querySelector('[data-slideshow-toggle]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.rotation = new SlideshowRotation({
      interval: this.dataset.interval,
      advance: () => {
        // The pictures after the first are on their way before any automatic move (loadSlides).
        this.loadSlides();
        this.controller.move(1);
      },
      onState: ({ requested, running }) => {
        this.toggle?.setAttribute('aria-label', requested ? this.dataset.pauseLabel : this.dataset.playLabel);
        this.toggle?.setAttribute('data-playing', String(requested));
        this.counter?.setAttribute('aria-live', running ? 'off' : 'polite');
        this.scheduleSlides(running);
      },
    });
    this.controller = new MediaTrack(this.track, this.slides, {
      rail,
      onIndex: (index) => this.select(index),
      onBusy: (busy) => {
        this.rotation.block('motion', busy);
        if (!busy && this.manualRecovery) {
          this.manualRecovery = false;
          this.rotation.resumeAfterNavigation();
        }
      },
      reduced: () => this.motion.matches,
    });
    this.setAttribute('data-enhanced', '');
    const initial = this.slides.findIndex((slide) => slide.dataset.blockId === selectedId);
    this.controller.jump(Math.max(0, initial));
    if (this.controls) this.controls.hidden = this.slides.length < 2;
    this.rotation.block('count', this.slides.length < 2);
    this.rotation.block('document', document.hidden);
    this.rotation.request(requested ?? (this.dataset.autoplay === 'true' && !this.motion.matches));
    // 2.9.1 (Owner): the first slide loads with the page; the pictures after it start together once the page has
    // loaded and gone idle, at the shopper's first press or keyboard focus here, or two seconds before the first
    // automatic move (scheduleSlides), whichever comes first.
    if (this.slidesWaiting) {
      const load = () => {
        if (!signal.aborted) this.loadSlides();
      };
      const idle = () =>
        typeof requestIdleCallback === 'function'
          ? requestIdleCallback(load, { timeout: 1000 })
          : setTimeout(load, 200);
      if (document.readyState === 'complete') idle();
      else listen(window, 'load', idle, { once: true });
      for (const type of ['pointerdown', 'focusin']) listen(this, type, load, { passive: true });
    }
    const step = (direction, fromGesture = false) => {
      if (!fromGesture && this.input?.pointer) this.input.finish(null, true);
      this.manualRecovery = true;
      this.rotation.block('motion', true);
      this.controller.move(direction);
    };
    listen(this.querySelector('[data-slideshow-prev]'), 'click', () => step(-1));
    listen(this.querySelector('[data-slideshow-next]'), 'click', () => step(1));
    listen(this.toggle, 'click', () => this.rotation.request(!this.rotation.requested));
    listen(this.controls, 'pointerenter', (e) => {
      if (e.pointerType === 'mouse') this.rotation.block('controls-hover', true);
    });
    listen(this.controls, 'pointerleave', (e) => {
      if (e.pointerType === 'mouse') this.rotation.block('controls-hover', false);
    });
    listen(this.controls, 'focusin', () => this.rotation.block('controls-focus', true));
    listen(this.controls, 'focusout', (e) => {
      if (!this.controls.contains(e.relatedTarget)) this.rotation.block('controls-focus', false);
    });
    // WCAG 2.2.2: a link or button inside the visible slide holds the slide while it has keyboard focus,
    // otherwise rotation would make that slide inert and drop focus. Focus on the track itself, and focus left
    // by a mouse or touch press (not :focus-visible), keep rotating as before.
    const slideControl = (node) => node && node !== this.track && this.track.contains(node);
    listen(this.track, 'focusin', (e) => {
      if (slideControl(e.target) && e.target.matches(':focus-visible')) this.rotation.block('slide-focus', true);
    });
    listen(this.track, 'focusout', (e) => {
      if (!slideControl(e.relatedTarget)) this.rotation.block('slide-focus', false);
    });
    listen(this.track, 'keydown', (e) => {
      if (e.target !== this.track) return;
      const sign = this.controller.rtl ? -1 : 1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        step((e.key === 'ArrowRight' ? 1 : -1) * sign);
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        this.goTo(e.key === 'Home' ? 0 : this.slides.length - 1, true, true);
      }
    });
    this.input = new TrackInput(this.track, this.controller, {
      onStart: () => {
        this.rotation.block('gesture', true);
      },
      onEnd: () => {
        // step already records manual navigation; release must not re-arm it.
        this.rotation.block('gesture', false);
      },
      // A recognized swipe still owns its horizontal gesture until release.
      onNavigate: (direction) => step(direction, true),
    });
    listen(document, 'visibilitychange', () => {
      if (document.hidden && this.input?.pointer) this.input.finish(null, true);
      this.rotation.block('document', document.hidden);
    });
    listen(window, 'pagehide', () => this.rotation.block('page', true));
    listen(window, 'pageshow', () => this.rotation.block('page', false));
    listen(this.motion, 'change', () => {
      if (this.motion.matches) {
        this.rotation.request(false);
        this.goTo(this.current, true);
      }
    });
    listen(this, 'shopify:block:select', (e) => {
      const index = this.slides.findIndex((s) => s.dataset.blockId === e.detail?.blockId || s === e.target);
      if (index >= 0) this.goTo(index, true);
    });
    const firstImage = this.slides[0].querySelector('img');
    const imageState = () => {
      if (firstImage?.complete && !firstImage.naturalWidth) this.track.style.aspectRatio = '16 / 9';
      else this.track.style.removeProperty('aspect-ratio');
      this.updateContentHeight();
    };
    listen(firstImage, 'load', imageState);
    listen(firstImage, 'error', imageState);
    imageState();
    const section = this.closest('.shopify-section');
    listen(section, 'shopify:section:unload', () => this.cleanup());
    listen(section, 'shopify:section:load', () => this.connectedCallback());
    listen(section, 'shopify:section:reorder', () => this.connectedCallback());
    // Cache geometry only at initialization/resize. Pixel transforms avoid
    // WebKit's fractional percentage-transform rounding on composited tracks.
    this.width = this.track.getBoundingClientRect().width;
    this.updateContentHeight();
    listen(document.fonts, 'loadingdone', () => this.updateContentHeight());
    if (globalThis.ResizeObserver) {
      this.resize = new ResizeObserver((entries) => {
        if (signal.aborted) return;
        const width = this.track.getBoundingClientRect().width;
        if (width !== this.width && this.input?.pointer) this.input.finish(null, true);
        this.width = width;
        this.controller.resize(width);
        this.updateContentHeight();
      });
      this.resize.observe(this.track);
      this.contentTargets.forEach((content) => this.resize.observe(content));
    }
    if (globalThis.IntersectionObserver) {
      // Autoplay advances only while the slideshow can be seen; off-screen ticks
      // would spend timers and animations and move slides nobody watched.
      this.visibility = new IntersectionObserver((entries) => {
        if (!signal.aborted) this.rotation.block('viewport', !entries.at(-1).isIntersecting);
      });
      this.visibility.observe(this);
    }
    this.controls?.querySelectorAll('button').forEach((button) => {
      button.disabled = false;
    });
  }
  updateContentHeight() {
    if (!this.track || this.abort?.signal.aborted) return;
    const [numerator, denominator = 1] = getComputedStyle(this.track)
      .aspectRatio.replace(/^auto\s*/, '')
      .split('/')
      .map(Number);
    const ratio = numerator / denominator;
    if (!Number.isFinite(ratio) || ratio <= 0) return;
    const baseHeight = this.track.getBoundingClientRect().width / ratio;
    if (!baseHeight) return;
    let height = 0;
    for (const content of this.contentTargets || []) {
      const natural = content.getBoundingClientRect();
      // An oversized grid row starts at the track top; smaller rows center.
      // Compare with the original ratio budget, never our expanded track.
      const offset = Math.max((baseHeight - natural.height) / 2, 0);
      const bounds = [];
      for (const element of content.querySelectorAll('h2, .prose')) {
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        for (let node; (node = walker.nextNode());) {
          if (!node.textContent.trim()) continue;
          const range = document.createRange();
          range.selectNodeContents(node);
          bounds.push(...range.getClientRects());
        }
      }
      const button = content.querySelector('[data-slide-button]');
      if (button?.getClientRects().length) bounds.push(button.getBoundingClientRect());
      if (
        bounds.some(
          (box) =>
            box.height && (box.top - natural.top + offset < -1 || box.bottom - natural.top + offset > baseHeight + 1),
        )
      )
        height = Math.max(height, natural.height);
    }
    // Avoid resize feedback and preserve fitting copy even when only its
    // surrounding padding exceeds the frame. Media keeps its original ratio.
    const value = height ? height + 'px' : '';
    if (this.track.style.getPropertyValue('--slideshow-content-min-height') !== value) {
      if (value) this.track.style.setProperty('--slideshow-content-min-height', value);
      else this.track.style.removeProperty('--slideshow-content-min-height');
    }
    const mediaRatio = String(ratio);
    if (this.track.style.getPropertyValue('--slideshow-media-ratio') !== mediaRatio)
      this.track.style.setProperty('--slideshow-media-ratio', mediaRatio);
  }
  select(index) {
    index = wrapSlide(index, this.slides.length);
    this.selectedBlockId = this.slides[index]?.dataset.blockId;
    this.slides.forEach((slide, i) => {
      const active = i === this.current;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
      slide.setAttribute(
        'aria-label',
        (this.dataset.positionLabel || '__current__ / __total__')
          .replace('__current__', i + 1)
          .replace('__total__', this.slides.length),
      );
    });
    // Neighbours preload for the next move at low priority, after the visible slide; while the pictures after the
    // first still wait for the page (loadSlides), they wait too.
    if (!this.slidesWaiting)
      for (const offset of [-1, 1]) {
        const image = this.slides[wrapSlide(this.current + offset, this.slides.length)]?.querySelector('img');
        if (image) {
          image.loading = 'eager';
          image.fetchPriority = 'low';
        }
      }
    if (this.counter)
      this.counter.textContent = (this.dataset.counterLabel || '__current__ / __total__')
        .replace('__current__', this.current + 1)
        .replace('__total__', this.slides.length);
  }
  // 2.9.1 (Owner): the pictures after the first slide wait (sections/slideshow.liquid) until loadSlides starts them.
  get slidesWaiting() {
    return Boolean(this.querySelector('[data-slide-deferred]')) && !this.hasAttribute('data-slides-ready');
  }
  // Autoplay moves only while the slideshow is on screen; its pictures start at the latest two seconds before the
  // first automatic move, so the next slide never arrives half loaded.
  scheduleSlides(running) {
    if (!running || !this.slidesWaiting) {
      clearTimeout(this.slidesTimer);
      this.slidesTimer = null;
      return;
    }
    if (this.slidesTimer) return;
    this.slidesTimer = setTimeout(
      () => {
        this.slidesTimer = null;
        this.loadSlides();
      },
      Math.max(0, this.rotation.interval - 2000),
    );
  }
  // Starts every picture after the first at once: the next slide's first, the others and the loop copies after it.
  loadSlides() {
    if (!this.slidesWaiting) return;
    this.setAttribute('data-slides-ready', '');
    clearTimeout(this.slidesTimer);
    this.slidesTimer = null;
    const next = this.slides[wrapSlide(this.current + 1, this.slides.length)];
    for (const image of this.querySelectorAll('[data-slide-deferred] img')) {
      image.loading = 'eager';
      image.fetchPriority = next?.contains(image) ? 'high' : 'low';
    }
    // The loop copies were built without their files (media-track.js inertCopy); they get them back last.
    restoreFiles(this);
  }
  goTo(index, instant = false, manual = false) {
    if (!this.controller) return;
    if (this.input?.pointer) this.input.finish(null, true);
    if (manual) {
      this.manualRecovery = true;
      this.rotation.resumeAfterNavigation();
    }
    this.controller.goTo(index, instant);
  }
  cleanup() {
    this.abort?.abort();
    clearTimeout(this.slidesTimer);
    this.slidesTimer = null;
    this.rotation?.dispose();
    this.resize?.disconnect();
    this.visibility?.disconnect();
    this.visibility = null;
    this.contentTargets = null;
    this.track?.style.removeProperty('--slideshow-content-min-height');
    this.track?.style.removeProperty('--slideshow-media-ratio');
    this.input?.dispose();
    this.input = null;
    this.controller?.dispose();
    this.controller = null;
    this.manualRecovery = false;
    this.controls?.querySelectorAll('button').forEach((button) => {
      button.disabled = true;
    });
    this.removeAttribute('data-enhanced');
    this.slides?.forEach((slide) => {
      slide.inert = false;
      slide.removeAttribute('aria-hidden');
    });
  }
  disconnectedCallback() {
    this.cleanup();
  }
}
if (globalThis.customElements && !customElements.get('meri-slideshow'))
  customElements.define('meri-slideshow', MeriSlideshow);
